import { resolveHotelLocationDescription } from "./ldsGetHotelInfoClient";

/**
 * Legacy helper for htlLocation arrays. Prefer resolveHotelLocationDescription.
 * Returns { text } or null (requestedLang is ignored; iw_il / en_us order matches SSOT).
 */
export function filterByLanguage(data, _requestedLang) {
  const text = resolveHotelLocationDescription(null, { htlLocation: data });
  return text ? { text } : null;
}

export function get_depret_from_result(result) {
  if (!result || !result.linkedFlights) {
    return { depDate: null, retDate: null };
  }
  
  try {
    const { linkedFlights } = result;
    const depLeg = linkedFlights?.flight?.[0]?.leg;
    const depDate = Array.isArray(depLeg) ? depLeg[0]?.depDate : depLeg?.depDate;
    
    const retLeg = linkedFlights?.flight?.[1]?.leg;
    const retDate = Array.isArray(retLeg) ? retLeg[0]?.depDate : retLeg?.depDate;
    
    return { depDate, retDate };
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[LDSHelpers] Error getting dates from result:', error);
    }
    return { depDate: null, retDate: null };
  }
}

export function parseToLdsDate(dateString) {
  if (!dateString || typeof dateString !== "string") {
    return ''; // Return empty string instead of null for backward compatibility
  }
  
  try {
    const datePart = dateString.split(" ")[0];
    const parts = datePart.split("/");
    if (parts.length !== 3) {
      return ''; // Return empty string instead of null
    }
    return `${parts[0]}/${parts[1]}/${parts[2]}`;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[LDSHelpers] Error parsing date:', error);
    }
    return ''; // Return empty string instead of null for backward compatibility
  }
}

export function countAdultsAndKids(roomsAndPaxInfo) {
  let totalAdults = 0;
  let totalKids = 0;
  let totalInfant = 0;

  if (!Array.isArray(roomsAndPaxInfo) || roomsAndPaxInfo.length === 0) {
    return { totalAdults, totalKids, totalInfant };
  }

  try {
    for (const group of roomsAndPaxInfo) {
      if (!group || typeof group !== 'object') continue;
      
      totalAdults += Array.isArray(group.adults) ? group.adults.length : 0;
      totalKids += Array.isArray(group.kids) ? group.kids.length : 0;
      totalInfant += Array.isArray(group.infants) ? group.infants.length : 0;
      // NOTE: LDS search only by the first room - there no counting
      break;
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[LDSHelpers] Error counting adults and kids:', error);
    }
  }

  return { totalAdults, totalKids, totalInfant };
}

export function getBiggestRoom(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo) || roomsAndPaxInfo.length === 0) {
    return { totalAdults: 0, totalKids: 0, totalInfant: 0 };
  }

  try {
    const biggestRoom = roomsAndPaxInfo.reduce((maxRoom, currentRoom) => {
      if (!currentRoom || typeof currentRoom !== 'object') return maxRoom;
      
      const currentAdults = Array.isArray(currentRoom.adults) ? currentRoom.adults.length : 0;
      const currentKids = Array.isArray(currentRoom.kids) ? currentRoom.kids.length : 0;
      const maxAdults = Array.isArray(maxRoom.adults) ? maxRoom.adults.length : 0;
      const maxKids = Array.isArray(maxRoom.kids) ? maxRoom.kids.length : 0;
      
      const currentTotal = currentAdults + currentKids;
      const maxTotal = maxAdults + maxKids;
      return currentTotal > maxTotal ? currentRoom : maxRoom;
    }, roomsAndPaxInfo[0] || { adults: [], kids: [], infants: [] });

    return {
      totalAdults: Array.isArray(biggestRoom.adults) ? biggestRoom.adults.length : 0,
      totalKids: Array.isArray(biggestRoom.kids) ? biggestRoom.kids.length : 0,
      totalInfant: Array.isArray(biggestRoom.infants) ? biggestRoom.infants.length : 0,
    };
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[LDSHelpers] Error getting biggest room:', error);
    }
    return { totalAdults: 0, totalKids: 0, totalInfant: 0 };
  }
}

export function getRoomSetByCount(adults, kids) {
  if (adults === 1 && kids > 0) {
    adults = 2;
    kids = kids - 1;
  }
  const key = "A" + adults + (kids > 0 ? "C" + kids : "");
  return key || null;
}

export function getRoomsSetByCount(adults, kids) {
  const keys = []
  if (adults === 1 && kids > 0) {
    adults = 2;
    kids = kids - 1;
  }
  let key = "A" + adults + (kids > 0 ? "C" + kids : "");
  keys.push(key)
  let another = "A" + (Number(adults) + Number(kids)).toString()
  if (key !== another) {
    keys.push(another)
    keys.push("P" + (Number(adults) + Number(kids)).toString())
  }
  return keys;
}

/** LDS often labels a couple DBL/TWIN instead of A2. Occupancy only — never a fare. */
const LDS_ROOM_SET_ALIASES = {
  DBL: "A2",
  TWIN: "A2",
  DOUBLE: "A2",
  SGL: "A1",
  SINGLE: "A1",
  TRP: "A3",
  TRIPLE: "A3",
  QUAD: "A4",
};

export function normalizeLdsRoomSetOccupancyKey(roomSet) {
  const raw = String(roomSet || "").trim().toUpperCase();
  if (!raw) return null;
  return LDS_ROOM_SET_ALIASES[raw] || raw;
}

export function ldsExtendedRoomMatchesSlotOccupancy({
  roomSet,
  adultsCount,
  kidsCount = 0,
} = {}) {
  const neededRoomSets = getRoomsSetByCount(
    Number(adultsCount) || 0,
    Number(kidsCount) || 0
  );
  const normalized = normalizeLdsRoomSetOccupancyKey(roomSet);
  if (!normalized) return false;
  return neededRoomSets.includes(normalized);
}

export function isLdsResponseEmpty(res) {
  return (
    res?.opStatus?.status === "NO_RESULTS" ||
    res?.data?.opStatus?.status === "NO_RESULTS" ||
    !res?.data
  );
}
